import React, {useEffect, useMemo, useState} from 'react';
import {DateTime} from 'luxon';
import moment, {Moment} from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker/dist';
import {Alert, Col, Nav, Row, Tab} from 'react-bootstrap';
import axios from 'axios';
import Icon from '@/components/icons/Icon';
import {BaseModal} from '@/components/modals';
import {buildAuditedStoryboardInsightsURL, buildURL} from '@/utils/urlBuilder';
import CellLoader from '@/components/loading/CellLoader';
import TableComponent from '@/components/table/Table';
import {Tooltip} from '@/components/tooltips';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import UserAuditSummaryCharts from './UserAuditSummaryCharts';
import UserAuditLoginsSessionsCharts from './UserAuditLoginsSessionsCharts';
import UserAuditSearchChart from './UserAuditSearchChart';
import UserAuditStoryboardAccessCharts from './UserAuditStoryboardAccessCharts';
import UserAuditAuditEventsTimeline from './UserAuditAuditEventsTimeline';
import UserAuditAuditEventSankey from './UserAuditAuditEventSankey';

const PATH_DISPLAY_MAX_LEN = 100;

function CategoryWithPathTooltip({
    category,
    path,
}: {
    category: string;
    path?: string | null | undefined;
}) {
    const fullPath = (path ?? '').trim();
    if (!fullPath) {
        return <span className="text-break">{category}</span>;
    }
    return (
        <Tooltip
            content={<span className="text-break d-inline-block" style={{maxWidth: 480}}>{fullPath}</span>}
            placement="top"
        >
            <span className="text-break d-inline-block" style={{cursor: 'help', maxWidth: '100%'}}>
                {category}
            </span>
        </Tooltip>
    );
}

function PathCell({value}: {value: string | null | undefined}) {
    const full = (value ?? '').trim();
    if (!full) {
        return <>-</>;
    }
    const isLong = full.length > PATH_DISPLAY_MAX_LEN;
    const display = isLong ? `${full.slice(0, PATH_DISPLAY_MAX_LEN)}…` : full;
    const body = (
        <span
            className="text-break d-inline-block"
            style={{maxWidth: '100%', cursor: isLong ? 'help' : undefined}}
        >
            {display}
        </span>
    );
    if (!isLong) {
        return body;
    }
    return (
        <Tooltip content={full} placement="top">
            {body}
        </Tooltip>
    );
}

function StoryboardHashPathCell({
    storyboardHashId,
    storyboardTitle,
    urlPathSegment,
}: {
    storyboardHashId: string | null | undefined;
    storyboardTitle?: string | null;
    urlPathSegment?: string | null;
}) {
    const hash = (storyboardHashId ?? '').trim();
    if (!hash) {
        return <>-</>;
    }
    const href = buildAuditedStoryboardInsightsURL(hash, storyboardTitle ?? null, urlPathSegment ?? null);
    return (
        <span className="d-inline-flex align-items-center flex-nowrap gap-1">
            <PathCell value={hash} />
            <Tooltip content={i18n.t('ub_open_storyboard_new_tab.text')} placement="top">
                <a
                    className="btn btn-link btn-sm p-0 align-middle text-info"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={i18n.t('ub_open_storyboard_new_tab.text')}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Icon name="mdi-open-in-new" size="sm" />
                </a>
            </Tooltip>
        </span>
    );
}

interface UserAuditModalProps {
    modalOpen: boolean;
    closeModal: () => void;
    activeUserId: number;
    auditedUserLabel?: string;
}

type AuditTabKey =
    | 'summary'
    | 'stories_created'
    | 'stories_accessed'
    | 'logins_sessions'
    | 'navigation'
    | 'events'
    | 'search'
    | 'transitions';

interface AnalyticsMetrics {
    totalSessions: number;
    totalEvents: number;
    avgSessionDurationSeconds: number;
    maxSessionDurationSeconds: number;
    totalSessionDurationSeconds: number;
    activeDaysCount: number;
    loginCount: number;
    storiesCreatedCount: number;
    storyboardsCreatedCount: number;
    sessionsTrendPercent: number | null;
    eventsTrendPercent: number | null;
}

interface SessionsOverTimeRow {
    date: string;
    sessionCount: number;
    totalSessionDurationSeconds: number;
    avgSessionDurationSeconds: number;
}

interface DailyLoginsSessionsRow {
    id: string;
    date: string;
    loginCount: number;
    sessionCount: number;
    totalSessionDurationSeconds: number;
    avgSessionDurationSeconds: number;
}

interface AnalyticsResponse {
    metrics: AnalyticsMetrics;
    activityOverTime: Array<{date: string; eventCount: number}>;
    loginsOverTime: Array<{date: string; loginCount: number}>;
    sessionsOverTime: SessionsOverTimeRow[];
    topPages: Array<{path: string; count: number}>;
    topModules?: Array<{module: string; count: number}>;
    topEndpoints?: Array<{module: string; auditEvent?: string | null; endpoint: string; count: number}>;
    topAuditEvents?: Array<{auditEvent: string; count: number}>;
    auditEventCounts?: Array<{auditEvent: string; count: number}>;
    auditEventTimeline?: {
        dayKeys: string[];
        series: Array<{auditEvent: string; data: number[]; isOther?: boolean}>;
    };
    topTools: Array<{tool: string; count: number}>;
    period: {start: string; end: string; calendarDayCount: number};
}

interface ModuleFlowRow {
    fromModule: string;
    toModule: string;
    fromPage: string;
    toPage: string;
    transitionCount: number;
    transitionPercent: number;
}

interface EndpointFlowRow {
    fromModule: string;
    fromEndpoint: string | null;
    fromAuditEvent?: string | null;
    toModule: string;
    toEndpoint: string | null;
    toAuditEvent?: string | null;
    fromPage: string;
    toPage: string;
    transitionCount: number;
    transitionPercent: number;
}

interface AuditEventSankeyPayload {
    nodes: Array<{name: string}>;
    links: Array<{source: string; target: string; value: number}>;
}

interface UserAuditFlowsResponse {
    moduleFlows: ModuleFlowRow[];
    flows: EndpointFlowRow[];
    auditEventSankey: AuditEventSankeyPayload;
}

interface AuditContentStoryRow {
    id: number;
    title: string;
    assignedUrl: string | null;
    createdOn: string | null;
}

interface AuditContentStoryboardRow {
    id: number;
    storyboardHashid?: string;
    title: string | null;
    storyId: number;
    storyTitle: string | null;
    sortIndex: number;
    createdOn: string | null;
}

interface StoryboardAccessTableRow {
    storyboardHashId: string;
    storyboardId: number | null;
    storyboardTitle: string | null;
    storyTitle: string | null;
    distinctLogHitsForHash: number;
    accessKind: string;
    urlPathSegment: string | null;
    requestCount: number;
    lastAccessOn: string | null;
}

interface AuditContentResponse {
    storiesCreatedCount: number;
    storyboardsCreatedCount: number;
    distinctStoriesWithNewStoryboards: number;
    stories: AuditContentStoryRow[];
    storyboards: AuditContentStoryboardRow[];
    storyboardAccessTable?: StoryboardAccessTableRow[];
    storyboardAccessChartPie?: Array<{
        label: string;
        value: number;
        storyboardHashId: string | null;
        isOther?: boolean;
    }>;
    storyboardAccessChartTimeline?: {
        dayKeys: string[];
        series: Array<{
            storyboardHashId: string | null;
            label: string;
            data: number[];
            isOther?: boolean;
        }>;
    };
    period: {start: string; end: string; calendarDayCount: number};
}

interface AuditSearchMetrics {
    searchLogCount: number;
    savedQueryCount: number;
    elasticsearchCount: number;
    redisCount: number;
    cachedSearchCount: number;
}

interface AuditSearchCountRow {
    value: string;
    count: number;
}

interface AuditSearchOverTimeRow {
    date: string;
    searchCount: number;
    avgQueryDurationSeconds: number | null;
    avgQueryExecutionSeconds: number | null;
}

interface AuditRecentSearchLogRow {
    id: number;
    createdOn: string | null;
    dataOrigin: string;
    wasCached: boolean;
    output: string | null;
    aggsItem: string | null;
    sortField: string | null;
    queryText: string | null;
    requestUrl: string | null;
}

interface AuditSavedQueryRow {
    id: number;
    createdOn: string | null;
    preview: string;
}

interface AuditSearchResponse {
    metrics: AuditSearchMetrics;
    searchesOverTime: AuditSearchOverTimeRow[];
    topOutputs: AuditSearchCountRow[];
    topAggsItems: AuditSearchCountRow[];
    topTextQueries: AuditSearchCountRow[];
    topSortFields: AuditSearchCountRow[];
    recentSearchLogs: AuditRecentSearchLogRow[];
    savedQueries: AuditSavedQueryRow[];
    period: {start: string; end: string; calendarDayCount: number};
}

function defaultThisAndLastMonthDdMmYyyy(): {start: string; end: string} {
    const end = moment().toDate().toLocaleDateString('en-GB');
    const start = moment().subtract(1, 'month').startOf('month').toDate().toLocaleDateString('en-GB');
    return {start, end};
}

function ddMmYyyyRangeToUtcIsoParams(
    startDdMmYyyy: string,
    endDdMmYyyy: string,
): {periodStart: string; periodEnd: string} | null {
    if (!startDdMmYyyy || !endDdMmYyyy) {
        return null;
    }
    const start = DateTime.fromFormat(startDdMmYyyy, 'dd/MM/yyyy', {zone: 'local'});
    const end = DateTime.fromFormat(endDdMmYyyy, 'dd/MM/yyyy', {zone: 'local'});
    if (!start.isValid || !end.isValid || start > end) {
        return null;
    }
    const periodStart = start.startOf('day').toUTC().toISO();
    const periodEnd = end.endOf('day').toUTC().toISO();
    if (!periodStart || !periodEnd) {
        return null;
    }
    return {periodStart, periodEnd};
}

function formatDurationSeconds(sec: number): string {
    if (sec == null || Number.isNaN(sec) || sec < 60) {
        return `${Math.round(sec || 0)}s`;
    }
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${m}m ${s}s`;
}

const STORYBOARD_ACCESS_KIND_MAP: Record<string, string> = {
    known_storyboard_hash: 'ub_sb_access_known_storyboard_hash.text',
    api_storyboard_numeric_id: 'ub_sb_access_api_storyboard_numeric_id.text',
};

function storyboardAccessKindLabel(kind: string): string {
    const key = STORYBOARD_ACCESS_KIND_MAP[kind];
    return key ? i18n.t(key) : kind;
}

function AuditMetricHelp({
    labelKey,
    tooltipKey,
}: {
    labelKey: string;
    tooltipKey: string;
}) {
    return (
        <Tooltip
            content={
                <span className="text-start d-inline-block" style={{maxWidth: 320}}>
                    {i18n.t(tooltipKey)}
                </span>
            }
            placement="top"
        >
            <div className="text-muted small" style={{cursor: 'help'}}>
                {i18n.t(labelKey)}
            </div>
        </Tooltip>
    );
}

const UserAuditModal: React.FC<UserAuditModalProps> = React.memo(({
    modalOpen,
    closeModal,
    activeUserId,
    auditedUserLabel,
}) => {
    const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
    const [activeTab, setActiveTab] = useState<AuditTabKey>('summary');
    const [dateRangeStart, setDateRangeStart] = useState(() => defaultThisAndLastMonthDdMmYyyy().start);
    const [dateRangeEnd, setDateRangeEnd] = useState(() => defaultThisAndLastMonthDdMmYyyy().end);
    const [auditDatePickerKey, setAuditDatePickerKey] = useState(0);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [loadingContent, setLoadingContent] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [loadingTransitions, setLoadingTransitions] = useState(false);
    const [overviewError, setOverviewError] = useState<string | null>(null);
    const [contentError, setContentError] = useState<string | null>(null);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [transitionsError, setTransitionsError] = useState<string | null>(null);
    const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
    const [moduleFlows, setModuleFlows] = useState<ModuleFlowRow[]>([]);
    const [endpointFlows, setEndpointFlows] = useState<EndpointFlowRow[]>([]);
    const [auditEventSankey, setAuditEventSankey] = useState<AuditEventSankeyPayload>({nodes: [], links: []});
    const [contentData, setContentData] = useState<AuditContentResponse | null>(null);
    const [searchData, setSearchData] = useState<AuditSearchResponse | null>(null);

    const periodParams = useMemo(
        () => ddMmYyyyRangeToUtcIsoParams(dateRangeStart, dateRangeEnd),
        [dateRangeStart, dateRangeEnd],
    );

    useEffect(() => {
        if (modalOpen) {
            const {start, end} = defaultThisAndLastMonthDdMmYyyy();
            setDateRangeStart(start);
            setDateRangeEnd(end);
            setAuditDatePickerKey((k) => k + 1);
            setActiveTab('summary');
            setOverviewError(null);
            setContentError(null);
            setSearchError(null);
            setTransitionsError(null);
            setAnalytics(null);
            setContentData(null);
            setSearchData(null);
            setModuleFlows([]);
            setEndpointFlows([]);
            setAuditEventSankey({nodes: [], links: []});
        }
    }, [modalOpen, activeUserId]);

    useEffect(() => {
        if (!modalOpen || activeUserId === 0 || !periodParams) {
            return;
        }
        let active = true;
        (async () => {
            setLoadingSummary(true);
            setOverviewError(null);
            try {
                const params = {...periodParams};
                const anRes = await axios.get<AnalyticsResponse>(
                    buildURL('userAuditAnalytics', {user_id: activeUserId}),
                    {params},
                );
                if (!active) {
                    return;
                }
                setAnalytics(anRes.data);
            } catch (e: unknown) {
                if (!active) {
                    return;
                }
                const msg = axios.isAxiosError(e) && e.response?.data?.error
                    ? String(e.response.data.error)
                    : i18n.t('jt_error_loading_data.text');
                setOverviewError(msg);
                setAnalytics(null);
            } finally {
                if (active) {
                    setLoadingSummary(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [modalOpen, activeUserId, periodParams]);

    useEffect(() => {
        if (!modalOpen || activeUserId === 0 || activeTab !== 'transitions' || !periodParams) {
            return;
        }
        let active = true;
        (async () => {
            setLoadingTransitions(true);
            setTransitionsError(null);
            setModuleFlows([]);
            setEndpointFlows([]);
            setAuditEventSankey({nodes: [], links: []});
            try {
                const params = {...periodParams};
                const flRes = await axios.get<UserAuditFlowsResponse>(
                    buildURL('userAuditFlows', {user_id: activeUserId}),
                    {params},
                );
                if (!active) {
                    return;
                }
                setModuleFlows(flRes.data.moduleFlows || []);
                setEndpointFlows(flRes.data.flows || []);
                setAuditEventSankey(
                    flRes.data.auditEventSankey ?? {nodes: [], links: []},
                );
            } catch (e: unknown) {
                if (!active) {
                    return;
                }
                const msg = axios.isAxiosError(e) && e.response?.data?.error
                    ? String(e.response.data.error)
                    : i18n.t('jt_error_loading_data.text');
                setTransitionsError(msg);
                setModuleFlows([]);
                setEndpointFlows([]);
                setAuditEventSankey({nodes: [], links: []});
            } finally {
                if (active) {
                    setLoadingTransitions(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [modalOpen, activeUserId, periodParams, activeTab]);

    useEffect(() => {
        if (
            !modalOpen
            || activeUserId === 0
            || (activeTab !== 'stories_created' && activeTab !== 'stories_accessed')
            || !periodParams
        ) {
            return;
        }
        let active = true;
        (async () => {
            setLoadingContent(true);
            setContentError(null);
            try {
                const params = {...periodParams};
                const res = await axios.get<AuditContentResponse>(
                    buildURL('userAuditContent', {user_id: activeUserId}),
                    {params},
                );
                if (!active) {
                    return;
                }
                setContentData(res.data);
            } catch (e: unknown) {
                if (!active) {
                    return;
                }
                const msg = axios.isAxiosError(e) && e.response?.data?.error
                    ? String(e.response.data.error)
                    : i18n.t('jt_error_loading_data.text');
                setContentError(msg);
                setContentData(null);
            } finally {
                if (active) {
                    setLoadingContent(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [modalOpen, activeUserId, periodParams, activeTab]);

    useEffect(() => {
        if (!modalOpen || activeUserId === 0 || activeTab !== 'search' || !periodParams) {
            return;
        }
        let active = true;
        (async () => {
            setLoadingSearch(true);
            setSearchError(null);
            try {
                const params = {...periodParams};
                const res = await axios.get<AuditSearchResponse>(
                    buildURL('userAuditSearch', {user_id: activeUserId}),
                    {params},
                );
                if (!active) {
                    return;
                }
                setSearchData(res.data);
            } catch (e: unknown) {
                if (!active) {
                    return;
                }
                const msg = axios.isAxiosError(e) && e.response?.data?.error
                    ? String(e.response.data.error)
                    : i18n.t('jt_error_loading_data.text');
                setSearchError(msg);
                setSearchData(null);
            } finally {
                if (active) {
                    setLoadingSearch(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [modalOpen, activeUserId, periodParams, activeTab]);

    const moduleFlowColumns = useMemo(
        () => [
            {
                id: 'fromMod',
                header: i18n.t('ub_flow_from.text'),
                accessorKey: 'fromModule' as const,
                cell: ({row}: {row: {original: ModuleFlowRow}}) => (
                    <CategoryWithPathTooltip
                        category={row.original.fromModule}
                        path={row.original.fromPage}
                    />
                ),
            },
            {
                id: 'toMod',
                header: i18n.t('ub_flow_to.text'),
                accessorKey: 'toModule' as const,
                cell: ({row}: {row: {original: ModuleFlowRow}}) => (
                    <CategoryWithPathTooltip
                        category={row.original.toModule}
                        path={row.original.toPage}
                    />
                ),
            },
            {id: 'cnt', header: i18n.t('ub_transitions.text'), accessorKey: 'transitionCount' as const},
            {
                id: 'pct',
                header: i18n.t('ub_flow_percent.text'),
                accessorFn: (row: ModuleFlowRow) => `${row.transitionPercent}%`,
            },
        ],
        [],
    );

    const endpointFlowColumns = useMemo(
        () => [
            {
                id: 'fromMod',
                header: i18n.t('ub_module.text'),
                accessorKey: 'fromModule' as const,
            },
            {
                id: 'fromAuditEv',
                header: `${i18n.t('ub_flow_from.text')} (${i18n.t('ub_audit_event.text')})`,
                accessorKey: 'fromAuditEvent' as const,
                cell: ({row}: {row: {original: EndpointFlowRow}}) => (
                    <PathCell value={row.original.fromAuditEvent ?? ''} />
                ),
            },
            {
                id: 'fromEp',
                header: `${i18n.t('ub_flow_from.text')} (${i18n.t('ub_endpoint.text')})`,
                accessorKey: 'fromEndpoint' as const,
                cell: ({row}: {row: {original: EndpointFlowRow}}) => (
                    <PathCell value={row.original.fromEndpoint} />
                ),
            },
            {
                id: 'toMod',
                header: `${i18n.t('ub_flow_to.text')} (${i18n.t('ub_module.text')})`,
                accessorKey: 'toModule' as const,
            },
            {
                id: 'toAuditEv',
                header: `${i18n.t('ub_flow_to.text')} (${i18n.t('ub_audit_event.text')})`,
                accessorKey: 'toAuditEvent' as const,
                cell: ({row}: {row: {original: EndpointFlowRow}}) => (
                    <PathCell value={row.original.toAuditEvent ?? ''} />
                ),
            },
            {
                id: 'toEp',
                header: `${i18n.t('ub_flow_to.text')} (${i18n.t('ub_endpoint.text')})`,
                accessorKey: 'toEndpoint' as const,
                cell: ({row}: {row: {original: EndpointFlowRow}}) => (
                    <PathCell value={row.original.toEndpoint} />
                ),
            },
            {id: 'cnt', header: i18n.t('ub_transitions.text'), accessorKey: 'transitionCount' as const},
            {
                id: 'pct',
                header: i18n.t('ub_flow_percent.text'),
                accessorFn: (row: EndpointFlowRow) => `${row.transitionPercent}%`,
            },
        ],
        [],
    );

    const topPageRows = analytics?.topPages?.map((r, i) => ({id: i, ...r})) ?? [];
    const topModuleRows = analytics?.topModules?.map((r, i) => ({id: i, ...r})) ?? [];
    const topEndpointRows = analytics?.topEndpoints?.map((r, i) => ({id: i, ...r})) ?? [];
    const topAuditEventRows = analytics?.topAuditEvents?.map((r, i) => ({id: i, ...r})) ?? [];
    const auditEventCountRows = analytics?.auditEventCounts?.map((r, i) => ({id: i, ...r})) ?? [];
    const topToolRows = analytics?.topTools?.map((r, i) => ({id: i, ...r})) ?? [];

    const topModuleColumns = useMemo(
        () => [
            {
                id: 'module',
                header: i18n.t('ub_module.text'),
                accessorKey: 'module' as const,
            },
            {id: 'count', header: i18n.t('ub_requests.text'), accessorKey: 'count' as const},
        ],
        [],
    );

    const topEndpointColumns = useMemo(
        () => [
            {
                id: 'module',
                header: i18n.t('ub_module.text'),
                accessorKey: 'module' as const,
            },
            {
                id: 'auditEvent',
                header: i18n.t('ub_audit_event.text'),
                accessorKey: 'auditEvent' as const,
                cell: ({row}: {row: {original: {auditEvent?: string | null}}}) => (
                    <PathCell value={row.original.auditEvent ?? ''} />
                ),
            },
            {
                id: 'endpoint',
                header: i18n.t('ub_endpoint.text'),
                accessorKey: 'endpoint' as const,
                cell: ({row}: {row: {original: {endpoint: string}}}) => (
                    <PathCell value={row.original.endpoint} />
                ),
            },
            {id: 'count', header: i18n.t('ub_requests.text'), accessorKey: 'count' as const},
        ],
        [],
    );

    const topAuditEventColumns = useMemo(
        () => [
            {
                id: 'auditEvent',
                header: i18n.t('ub_audit_event.text'),
                accessorKey: 'auditEvent' as const,
                cell: ({row}: {row: {original: {auditEvent: string}}}) => (
                    <PathCell value={row.original.auditEvent} />
                ),
            },
            {id: 'count', header: i18n.t('ub_requests.text'), accessorKey: 'count' as const},
        ],
        [],
    );

    const topPageColumns = useMemo(
        () => [
            {
                id: 'path',
                header: i18n.t('th_path.text'),
                accessorKey: 'path' as const,
                cell: ({row}: {row: {original: {path: string}}}) => <PathCell value={row.original.path} />,
            },
            {id: 'count', header: i18n.t('ub_requests.text'), accessorKey: 'count' as const},
        ],
        [],
    );

    const topToolColumns = useMemo(
        () => [
            {id: 'tool', header: i18n.t('ub_tool.text'), accessorKey: 'tool' as const},
            {id: 'count', header: i18n.t('ub_requests.text'), accessorKey: 'count' as const},
        ],
        [],
    );

    const contentStoryColumns = useMemo(
        () => [
            {id: 'id', header: i18n.t('th_text_id_column.text'), accessorKey: 'id' as const},
            {id: 'title', header: i18n.t('jt_story_title.text'), accessorKey: 'title' as const},
            {
                id: 'assignedUrl',
                header: i18n.t('th_path.text'),
                accessorKey: 'assignedUrl' as const,
                cell: ({row}: {row: {original: AuditContentStoryRow}}) => <PathCell value={row.original.assignedUrl} />,
            },
            {
                id: 'createdOn',
                header: i18n.t('th_date_created.text'),
                accessorFn: (row: AuditContentStoryRow) => {
                    if (!row.createdOn) {
                        return '-';
                    }
                    return configLoaded ? formatDateTime(row.createdOn, true, {setZone: false}) : '';
                },
            },
        ],
        [configLoaded, formatDateTime],
    );

    const contentStoryboardColumns = useMemo(
        () => [
            {id: 'id', header: i18n.t('th_text_id_column.text'), accessorKey: 'id' as const},
            {
                id: 'storyboardHashid',
                header: i18n.t('ub_th_storyboard_hash.text'),
                accessorKey: 'storyboardHashid' as const,
                cell: ({row}: {row: {original: AuditContentStoryboardRow}}) => (
                    <StoryboardHashPathCell
                        storyboardHashId={row.original.storyboardHashid}
                        storyboardTitle={row.original.title}
                    />
                ),
            },
            {id: 'title', header: i18n.t('jt_storyboard_title.text'), accessorKey: 'title' as const},
            {id: 'storyId', header: i18n.t('ub_th_story_id.text'), accessorKey: 'storyId' as const},
            {id: 'storyTitle', header: i18n.t('jt_story_title.text'), accessorKey: 'storyTitle' as const},
            {id: 'sortIndex', header: i18n.t('ub_th_sort_index.text'), accessorKey: 'sortIndex' as const},
            {
                id: 'createdOn',
                header: i18n.t('th_date_created.text'),
                accessorFn: (row: AuditContentStoryboardRow) => {
                    if (!row.createdOn) {
                        return '-';
                    }
                    return configLoaded ? formatDateTime(row.createdOn, true, {setZone: false}) : '';
                },
            },
        ],
        [configLoaded, formatDateTime],
    );

    const storyboardAccessTableColumns = useMemo(
        () => [
            {
                id: 'storyboardHashId',
                header: i18n.t('ub_th_storyboard_hash.text'),
                accessorKey: 'storyboardHashId' as const,
                cell: ({row}: {row: {original: StoryboardAccessTableRow}}) => (
                    <StoryboardHashPathCell
                        storyboardHashId={row.original.storyboardHashId}
                        storyboardTitle={row.original.storyboardTitle}
                        urlPathSegment={row.original.urlPathSegment}
                    />
                ),
            },
            {id: 'storyboardId', header: i18n.t('th_text_id_column.text'), accessorKey: 'storyboardId' as const},
            {id: 'storyTitle', header: i18n.t('jt_story_title.text'), accessorKey: 'storyTitle' as const},
            {
                id: 'storyboardTitle',
                header: i18n.t('jt_storyboard_title.text'),
                accessorKey: 'storyboardTitle' as const,
            },
            {
                id: 'distinctLogHitsForHash',
                header: i18n.t('ub_distinct_log_hits_storyboard.text'),
                accessorKey: 'distinctLogHitsForHash' as const,
            },
            {
                id: 'accessKind',
                header: i18n.t('ub_access_kind.text'),
                accessorFn: (row: StoryboardAccessTableRow) => storyboardAccessKindLabel(row.accessKind),
            },
            {
                id: 'urlPathSegment',
                header: i18n.t('ub_th_url_path_segment.text'),
                accessorKey: 'urlPathSegment' as const,
                cell: ({row}: {row: {original: StoryboardAccessTableRow}}) => (
                    <PathCell value={row.original.urlPathSegment} />
                ),
            },
            {id: 'requestCount', header: i18n.t('ub_request_count.text'), accessorKey: 'requestCount' as const},
            {
                id: 'lastAccessOn',
                header: i18n.t('ub_th_last_access_log.text'),
                accessorFn: (row: StoryboardAccessTableRow) => {
                    if (!row.lastAccessOn) {
                        return '-';
                    }
                    return configLoaded ? formatDateTime(row.lastAccessOn, true, {setZone: false}) : '';
                },
            },
        ],
        [configLoaded, formatDateTime],
    );

    const storyboardAccessTableRows = useMemo(
        () =>
            (contentData?.storyboardAccessTable ?? []).map((r, i) => ({
                id: `${i}-${r.storyboardHashId}-${r.accessKind}-${r.urlPathSegment ?? ''}`,
                ...r,
            })),
        [contentData],
    );

    const storyboardAccessSummary = useMemo(() => {
        const rows = contentData?.storyboardAccessTable ?? [];
        const storyboardHashes = new Set(rows.map((r) => r.storyboardHashId));
        const storyTitles = new Set(
            rows
                .map((r) => r.storyTitle)
                .filter((t): t is string => Boolean(t && String(t).trim())),
        );
        const totalAccessEvents = rows.reduce((sum, r) => sum + (r.requestCount ?? 0), 0);
        return {
            distinctStoryboards: storyboardHashes.size,
            distinctStories: storyTitles.size,
            totalAccessEvents,
        };
    }, [contentData]);

    const summaryChartPayload = useMemo(() => {
        const loginsSeries = analytics?.loginsOverTime;
        const requestsSeries = analytics?.activityOverTime;
        if (!loginsSeries?.length || !requestsSeries?.length || loginsSeries.length !== requestsSeries.length) {
            return null;
        }
        const dayKeys = loginsSeries.map((r) => r.date);
        const axisLabels = loginsSeries.map((row) => {
            try {
                return DateTime.fromISO(row.date, {zone: 'utc'}).toLocaleString({
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return row.date.length >= 10 ? row.date.slice(5) : row.date;
            }
        });
        return {
            dayKeys,
            axisLabels,
            loginCounts: loginsSeries.map((r) => r.loginCount),
            requestCounts: requestsSeries.map((r) => r.eventCount),
        };
    }, [analytics]);

    const loginsSessionsPayload = useMemo(() => {
        const loginsSeries = analytics?.loginsOverTime;
        const sessionsSeries = analytics?.sessionsOverTime;
        if (!loginsSeries?.length || !sessionsSeries?.length || loginsSeries.length !== sessionsSeries.length) {
            return null;
        }
        for (let i = 0; i < loginsSeries.length; i++) {
            if (loginsSeries[i].date !== sessionsSeries[i].date) {
                return null;
            }
        }
        const dayKeys = loginsSeries.map((r) => r.date);
        const axisLabels = loginsSeries.map((row) => {
            try {
                return DateTime.fromISO(row.date, {zone: 'utc'}).toLocaleString({
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return row.date.length >= 10 ? row.date.slice(5) : row.date;
            }
        });
        const tableRows: DailyLoginsSessionsRow[] = loginsSeries.map((r, i) => ({
            id: r.date,
            date: r.date,
            loginCount: r.loginCount,
            sessionCount: sessionsSeries[i].sessionCount,
            totalSessionDurationSeconds: sessionsSeries[i].totalSessionDurationSeconds,
            avgSessionDurationSeconds: sessionsSeries[i].avgSessionDurationSeconds,
        }));
        return {
            dayKeys,
            axisLabels,
            loginCounts: loginsSeries.map((r) => r.loginCount),
            sessionCounts: sessionsSeries.map((r) => r.sessionCount),
            totalSessionDurationSeconds: sessionsSeries.map((r) => r.totalSessionDurationSeconds),
            avgSessionDurationSeconds: sessionsSeries.map((r) => r.avgSessionDurationSeconds),
            tableRows,
        };
    }, [analytics]);

    const dailyLoginsSessionsColumns = useMemo(
        () => [
            {
                id: 'date',
                header: i18n.t('th_date.text'),
                accessorFn: (row: DailyLoginsSessionsRow) => {
                    try {
                        return DateTime.fromISO(row.date, {zone: 'utc'}).toLocaleString(DateTime.DATE_MED);
                    } catch {
                        return row.date;
                    }
                },
            },
            {
                id: 'loginCount',
                header: i18n.t('ub_chart_logins_series.text'),
                accessorKey: 'loginCount' as const,
            },
            {
                id: 'sessionCount',
                header: i18n.t('ub_chart_sessions_series.text'),
                accessorKey: 'sessionCount' as const,
            },
            {
                id: 'totalSessionDurationSeconds',
                header: i18n.t('ub_total_session_time.text'),
                accessorFn: (row: DailyLoginsSessionsRow) =>
                    formatDurationSeconds(row.totalSessionDurationSeconds),
            },
            {
                id: 'avgSessionDurationSeconds',
                header: i18n.t('ub_avg_session.text'),
                accessorFn: (row: DailyLoginsSessionsRow) =>
                    formatDurationSeconds(row.avgSessionDurationSeconds),
            },
        ],
        [],
    );

    const searchChartPayload = useMemo(() => {
        const s = searchData?.searchesOverTime;
        if (!s?.length) {
            return null;
        }
        const dayKeys = s.map((r) => r.date);
        const axisLabels = s.map((row) => {
            try {
                return DateTime.fromISO(row.date, {zone: 'utc'}).toLocaleString({
                    month: 'short',
                    day: 'numeric',
                });
            } catch {
                return row.date.length >= 10 ? row.date.slice(5) : row.date;
            }
        });
        return {
            dayKeys,
            axisLabels,
            searchCounts: s.map((r) => r.searchCount),
            avgQueryDurationSeconds: s.map((r) =>
                (r.avgQueryDurationSeconds != null ? r.avgQueryDurationSeconds : null)),
            avgQueryExecutionSeconds: s.map((r) =>
                (r.avgQueryExecutionSeconds != null ? r.avgQueryExecutionSeconds : null)),
        };
    }, [searchData]);

    const topSearchOutputRows = searchData?.topOutputs?.map((r, i) => ({id: i, ...r})) ?? [];
    const topSearchAggsRows = searchData?.topAggsItems?.map((r, i) => ({id: i, ...r})) ?? [];
    const topSearchTextRows = searchData?.topTextQueries?.map((r, i) => ({id: i, ...r})) ?? [];
    const topSearchSortRows = searchData?.topSortFields?.map((r, i) => ({id: i, ...r})) ?? [];

    const searchTopColumns = useMemo(
        () => [
            {
                id: 'value',
                header: i18n.t('ub_search_value.text'),
                accessorKey: 'value' as const,
                cell: ({row}: {row: {original: AuditSearchCountRow}}) => (
                    <PathCell value={row.original.value} />
                ),
            },
            {id: 'count', header: i18n.t('ub_requests.text'), accessorKey: 'count' as const},
        ],
        [],
    );

    const recentSearchLogColumns = useMemo(
        () => [
            {
                id: 'createdOn',
                header: i18n.t('th_date_created.text'),
                accessorFn: (row: AuditRecentSearchLogRow) => {
                    if (!row.createdOn) {
                        return '-';
                    }
                    return configLoaded ? formatDateTime(row.createdOn, true, {setZone: false}) : '';
                },
            },
            {
                id: 'dataOrigin',
                header: i18n.t('ub_search_origin.text'),
                accessorKey: 'dataOrigin' as const,
            },
            {
                id: 'wasCached',
                header: i18n.t('ub_search_cached.text'),
                accessorFn: (row: AuditRecentSearchLogRow) =>
                    (row.wasCached ? i18n.t('jt_yes.text') : i18n.t('jt_no.text')),
            },
            {id: 'output', header: i18n.t('ub_th_search_output.text'), accessorKey: 'output' as const},
            {id: 'aggsItem', header: i18n.t('ub_th_search_aggs.text'), accessorKey: 'aggsItem' as const},
            {id: 'sortField', header: i18n.t('ub_th_search_sort.text'), accessorKey: 'sortField' as const},
            {
                id: 'queryText',
                header: i18n.t('ub_th_search_query_text.text'),
                accessorKey: 'queryText' as const,
                cell: ({row}: {row: {original: AuditRecentSearchLogRow}}) => (
                    <PathCell value={row.original.queryText} />
                ),
            },
            {
                id: 'requestUrl',
                header: i18n.t('th_path.text'),
                accessorKey: 'requestUrl' as const,
                cell: ({row}: {row: {original: AuditRecentSearchLogRow}}) => (
                    <PathCell value={row.original.requestUrl} />
                ),
            },
        ],
        [configLoaded, formatDateTime],
    );

    const savedSearchQueryColumns = useMemo(
        () => [
            {id: 'id', header: i18n.t('th_text_id_column.text'), accessorKey: 'id' as const},
            {
                id: 'createdOn',
                header: i18n.t('th_date_created.text'),
                accessorFn: (row: AuditSavedQueryRow) => {
                    if (!row.createdOn) {
                        return '-';
                    }
                    return configLoaded ? formatDateTime(row.createdOn, true, {setZone: false}) : '';
                },
            },
            {
                id: 'preview',
                header: i18n.t('ub_search_preview.text'),
                accessorKey: 'preview' as const,
                cell: ({row}: {row: {original: AuditSavedQueryRow}}) => (
                    <PathCell value={row.original.preview} />
                ),
            },
        ],
        [configLoaded, formatDateTime],
    );

    const modalTitle = useMemo(() => {
        const base = i18n.t('jt_user_audit.text');
        const label = (auditedUserLabel ?? '').trim();
        return label ? `${base} — ${label}` : base;
    }, [auditedUserLabel]);

    return (
        <BaseModal
            isOpen={modalOpen}
            onClose={closeModal}
            title={modalTitle}
            size="xl"
        >
            <div className="mb-3 d-flex flex-wrap align-items-center justify-content-end w-100 gap-2">
                <DateRangePicker
                    key={auditDatePickerKey}
                    initialSettings={{
                        startDate: dateRangeStart,
                        endDate: dateRangeEnd,
                        locale: {
                            format: 'DD/MM/YYYY',
                            firstDay: 1,
                            customRangeLabel: i18n.t('date_custom.text'),
                        },
                        showWeekNumbers: true,
                        opens: 'left',
                        alwaysShowCalendars: true,
                        maxDate: moment().format('DD/MM/YYYY'),
                        ranges: {
                            [i18n.t('date_today.text')]: [moment(), moment()],
                            [i18n.t('date_yesterday.text')]: [
                                moment().subtract(1, 'days'),
                                moment().subtract(1, 'days'),
                            ],
                            [i18n.t('date_last_week.text')]: [
                                moment().subtract(1, 'week').startOf('isoWeek'),
                                moment().subtract(1, 'week').endOf('isoWeek'),
                            ],
                            [i18n.t('date_last_month.text')]: [
                                moment().subtract(1, 'month').startOf('month'),
                                moment().subtract(1, 'month').endOf('month'),
                            ],
                            [i18n.t('date_this_and_last_month.text')]: [
                                moment().subtract(1, 'month').startOf('month'),
                                moment(),
                            ],
                            [i18n.t('date_last_quarter.text')]: [
                                moment().subtract(1, 'quarter').startOf('quarter'),
                                moment().subtract(1, 'quarter').endOf('quarter'),
                            ],
                            [i18n.t('date_this_year.text')]: [moment().startOf('year'), moment()],
                        },
                    }}
                    onCallback={(start: Moment, end: Moment) => {
                        setDateRangeStart(start.toDate().toLocaleDateString('en-GB'));
                        setDateRangeEnd(end.toDate().toLocaleDateString('en-GB'));
                    }}
                    onApply={(_event: unknown, picker: {startDate: Moment; endDate: Moment}) => {
                        setDateRangeStart(picker.startDate.toDate().toLocaleDateString('en-GB'));
                        setDateRangeEnd(picker.endDate.toDate().toLocaleDateString('en-GB'));
                    }}
                >
                    <h6
                        className="mb-0 d-inline-flex align-items-center gap-1"
                        style={{cursor: 'pointer'}}
                        data-cy="user-audit-date-range-trigger"
                    >
                        {i18n.t('dates.text')}: {dateRangeStart}-{dateRangeEnd}
                        <Icon name="mdi-chevron-down" />
                    </h6>
                </DateRangePicker>
            </div>

            <Tab.Container
                activeKey={activeTab}
                onSelect={(key) => {
                    if (
                        key === 'summary'
                        || key === 'stories_created'
                        || key === 'stories_accessed'
                        || key === 'logins_sessions'
                        || key === 'navigation'
                        || key === 'events'
                        || key === 'search'
                        || key === 'transitions'
                    ) {
                        setActiveTab(key);
                    }
                }}
            >
                <Nav variant="tabs" className="mb-3">
                    <Nav.Item>
                        <Nav.Link eventKey="summary" className="custom-tabs">
                            {i18n.t('ub_tab_summary.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="stories_created" className="custom-tabs">
                            {i18n.t('ub_tab_stories_created.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="stories_accessed" className="custom-tabs">
                            {i18n.t('ub_tab_stories_accessed.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="logins_sessions" className="custom-tabs">
                            {i18n.t('ub_tab_logins_sessions.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="navigation" className="custom-tabs">
                            {i18n.t('ub_tab_navigation.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="events" className="custom-tabs">
                            {i18n.t('ub_tab_events.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="search" className="custom-tabs">
                            {i18n.t('ub_tab_search.text')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="transitions" className="custom-tabs">
                            {i18n.t('ub_tab_transitions.text')}
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey="summary">
                        {overviewError ? <Alert variant="danger">{overviewError}</Alert> : null}
                        {loadingSummary ? <CellLoader /> : null}
                        {activeTab === 'summary' && analytics && !loadingSummary && summaryChartPayload ? (
                            <UserAuditSummaryCharts
                                dayKeys={summaryChartPayload.dayKeys}
                                axisLabels={summaryChartPayload.axisLabels}
                                loginCounts={summaryChartPayload.loginCounts}
                                requestCounts={summaryChartPayload.requestCounts}
                            />
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="stories_created">
                        {contentError ? <Alert variant="danger">{contentError}</Alert> : null}
                        {loadingContent ? <CellLoader /> : null}
                        {contentData && !loadingContent ? (
                            <>
                                <p className="small text-muted mb-3">{i18n.t('ub_content_tables_hint.text')}</p>
                                <Row className="g-2 mb-3">
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_stories_created.text"
                                                tooltipKey="ub_tt_stories_created.text"
                                            />
                                            <div className="fs-4 fw-semibold">{contentData.storiesCreatedCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_storyboards_created.text"
                                                tooltipKey="ub_tt_storyboards_created.text"
                                            />
                                            <div className="fs-4 fw-semibold">{contentData.storyboardsCreatedCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <div className="text-muted small" style={{cursor: 'default'}}>
                                                {i18n.t('ub_distinct_stories_new_boards.text')}
                                            </div>
                                            <div className="fs-4 fw-semibold">
                                                {contentData.distinctStoriesWithNewStoryboards}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <h6 className="mb-2">{i18n.t('ub_section_stories_table.text')}</h6>
                                <div className="card mb-3">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={contentData.stories}
                                            columns={contentStoryColumns}
                                        />
                                    </div>
                                </div>
                                <h6 className="mb-2">{i18n.t('ub_section_storyboards_table.text')}</h6>
                                <div className="card">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={contentData.storyboards}
                                            columns={contentStoryboardColumns}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="stories_accessed">
                        {contentError ? <Alert variant="danger">{contentError}</Alert> : null}
                        {loadingContent ? <CellLoader /> : null}
                        {contentData && !loadingContent ? (
                            <>
                                <p className="small text-muted mb-3">{i18n.t('ub_storyboard_access_hint.text')}</p>
                                <Row className="g-2 mb-3">
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_stories_accessed_distinct_storyboards.text"
                                                tooltipKey="ub_tt_stories_accessed_distinct_storyboards.text"
                                            />
                                            <div className="fs-4 fw-semibold">
                                                {storyboardAccessSummary.distinctStoryboards}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_stories_accessed_distinct_stories.text"
                                                tooltipKey="ub_tt_stories_accessed_distinct_stories.text"
                                            />
                                            <div className="fs-4 fw-semibold">
                                                {storyboardAccessSummary.distinctStories}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_stories_accessed_total_events.text"
                                                tooltipKey="ub_tt_stories_accessed_total_events.text"
                                            />
                                            <div className="fs-4 fw-semibold">
                                                {storyboardAccessSummary.totalAccessEvents}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <UserAuditStoryboardAccessCharts
                                    pieSlices={contentData.storyboardAccessChartPie ?? []}
                                    dayKeys={contentData.storyboardAccessChartTimeline?.dayKeys ?? []}
                                    timelineSeries={contentData.storyboardAccessChartTimeline?.series ?? []}
                                />
                                <h6 className="mb-2">{i18n.t('ub_section_storyboard_access_unified.text')}</h6>
                                <div className="card">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={storyboardAccessTableRows}
                                            columns={storyboardAccessTableColumns}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="logins_sessions">
                        {overviewError ? <Alert variant="danger">{overviewError}</Alert> : null}
                        {loadingSummary ? <CellLoader /> : null}
                        {analytics && !loadingSummary && loginsSessionsPayload ? (
                            <>
                                <p className="small text-muted mb-3">{i18n.t('ub_tt_audit_logs_scope.text')}</p>
                                <UserAuditLoginsSessionsCharts
                                    dayKeys={loginsSessionsPayload.dayKeys}
                                    axisLabels={loginsSessionsPayload.axisLabels}
                                    loginCounts={loginsSessionsPayload.loginCounts}
                                    sessionCounts={loginsSessionsPayload.sessionCounts}
                                    totalSessionDurationSeconds={loginsSessionsPayload.totalSessionDurationSeconds}
                                    avgSessionDurationSeconds={loginsSessionsPayload.avgSessionDurationSeconds}
                                />
                                <h6 className="mb-2">{i18n.t('ub_section_daily_logins_sessions.text')}</h6>
                                <div className="card">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={loginsSessionsPayload.tableRows}
                                            columns={dailyLoginsSessionsColumns}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                        {!loadingSummary && analytics && !loginsSessionsPayload ? (
                            <p className="text-muted mb-0">{i18n.t('jt_error_loading_data.text')}</p>
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="navigation">
                        {overviewError ? <Alert variant="danger">{overviewError}</Alert> : null}
                        {loadingSummary ? <CellLoader /> : null}
                        {analytics && !loadingSummary ? (
                            <>
                                <p className="small text-muted mb-2">{i18n.t('ub_navigation_tab_hint.text')}</p>
                                <p className="small text-muted mb-4">{i18n.t('ub_tt_audit_logs_scope.text')}</p>

                                <Row className="g-2 mb-4">
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp labelKey="ub_sessions.text" tooltipKey="ub_tt_sessions.text" />
                                            <div className="fs-4 fw-semibold">{analytics.metrics.totalSessions}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp labelKey="ub_api_requests.text" tooltipKey="ub_tt_api_requests.text" />
                                            <div className="fs-4 fw-semibold">{analytics.metrics.totalEvents}</div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp labelKey="ub_avg_session.text" tooltipKey="ub_tt_avg_session.text" />
                                            <div className="fs-4 fw-semibold">
                                                {formatDurationSeconds(analytics.metrics.avgSessionDurationSeconds)}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="g-2 mb-4">
                                    <Col md={6} lg={3}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp labelKey="ub_active_days.text" tooltipKey="ub_tt_active_days.text" />
                                            <div className="fs-4 fw-semibold">{analytics.metrics.activeDaysCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={3}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp labelKey="ub_logins_period.text" tooltipKey="ub_tt_logins_period.text" />
                                            <div className="fs-4 fw-semibold">{analytics.metrics.loginCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={3}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_total_session_time.text"
                                                tooltipKey="ub_tt_total_session_time.text"
                                            />
                                            <div className="fs-4 fw-semibold">
                                                {formatDurationSeconds(analytics.metrics.totalSessionDurationSeconds)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={3}>
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_longest_session.text"
                                                tooltipKey="ub_tt_longest_session.text"
                                            />
                                            <div className="fs-4 fw-semibold">
                                                {formatDurationSeconds(analytics.metrics.maxSessionDurationSeconds)}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <h6 className="mb-3 fw-semibold">{i18n.t('ub_navigation_breakdown.text')}</h6>

                                <Row className="g-3 mb-3">
                                    <Col lg={6}>
                                        <div className="card h-100">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_top_modules.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topModuleRows} columns={topModuleColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col lg={6}>
                                        <div className="card h-100">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_top_endpoints.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topEndpointRows} columns={topEndpointColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="g-3 mb-3">
                                    <Col xs={12}>
                                        <div className="card">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_top_audit_events.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topAuditEventRows} columns={topAuditEventColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="g-3 mb-3">
                                    <Col xs={12}>
                                        <div className="card">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_top_tools.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topToolRows} columns={topToolColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="g-3">
                                    <Col xs={12}>
                                        <div className="card">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_top_pages.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topPageRows} columns={topPageColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="events">
                        {overviewError ? <Alert variant="danger">{overviewError}</Alert> : null}
                        {loadingSummary ? <CellLoader /> : null}
                        {analytics && !loadingSummary ? (
                            <>
                                <p className="small text-muted mb-3">{i18n.t('ub_events_tab_hint.text')}</p>
                                <h6 className="mb-2 fw-semibold">{i18n.t('ub_audit_events_timeline_title.text')}</h6>
                                {analytics.auditEventTimeline?.series?.length ? (
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <UserAuditAuditEventsTimeline
                                                dayKeys={analytics.auditEventTimeline.dayKeys ?? []}
                                                series={analytics.auditEventTimeline.series ?? []}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-muted small mb-4">{i18n.t('ub_audit_events_timeline_empty.text')}</p>
                                )}
                                <h6 className="mb-2 fw-semibold">{i18n.t('ub_audit_events_counts_title.text')}</h6>
                                <div className="card">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={auditEventCountRows}
                                            columns={topAuditEventColumns}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="search">
                        {searchError ? <Alert variant="danger">{searchError}</Alert> : null}
                        {loadingSearch ? <CellLoader /> : null}
                        {searchData && !loadingSearch ? (
                            <>
                                <p className="small text-muted mb-4">{i18n.t('ub_search_tab_hint.text')}</p>

                                <Row>
                                    <Col md={6} lg={4} className="mb-3">
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_search_log_total.text"
                                                tooltipKey="ub_tt_search_log_total.text"
                                            />
                                            <div className="fs-4 fw-semibold">{searchData.metrics.searchLogCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4} className="mb-3">
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_search_saved_total.text"
                                                tooltipKey="ub_tt_search_saved_total.text"
                                            />
                                            <div className="fs-4 fw-semibold">{searchData.metrics.savedQueryCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4} className="mb-3">
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_search_elasticsearch_runs.text"
                                                tooltipKey="ub_tt_search_elasticsearch_runs.text"
                                            />
                                            <div className="fs-4 fw-semibold">
                                                {searchData.metrics.elasticsearchCount}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4} className="mb-3">
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_search_redis_runs.text"
                                                tooltipKey="ub_tt_search_redis_runs.text"
                                            />
                                            <div className="fs-4 fw-semibold">{searchData.metrics.redisCount}</div>
                                        </div>
                                    </Col>
                                    <Col md={6} lg={4} className="mb-3">
                                        <div className="border rounded p-3 h-100 bg-light">
                                            <AuditMetricHelp
                                                labelKey="ub_search_cached_hits.text"
                                                tooltipKey="ub_tt_search_cached_hits.text"
                                            />
                                            <div className="fs-4 fw-semibold">{searchData.metrics.cachedSearchCount}</div>
                                        </div>
                                    </Col>
                                </Row>

                                {searchChartPayload ? (
                                    <div className="mb-4">
                                        <UserAuditSearchChart
                                            dayKeys={searchChartPayload.dayKeys}
                                            axisLabels={searchChartPayload.axisLabels}
                                            searchCounts={searchChartPayload.searchCounts}
                                            avgQueryDurationSeconds={searchChartPayload.avgQueryDurationSeconds}
                                            avgQueryExecutionSeconds={searchChartPayload.avgQueryExecutionSeconds}
                                        />
                                    </div>
                                ) : null}

                                <h6 className="mb-3 fw-semibold">{i18n.t('ub_search_parameters_section.text')}</h6>

                                <Row className="g-3 mb-3">
                                    <Col lg={6}>
                                        <div className="card h-100">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_search_top_outputs.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topSearchOutputRows} columns={searchTopColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col lg={6}>
                                        <div className="card h-100">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_search_top_aggs.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topSearchAggsRows} columns={searchTopColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="g-3 mb-3">
                                    <Col lg={6}>
                                        <div className="card h-100">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_search_top_queries.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topSearchTextRows} columns={searchTopColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col lg={6}>
                                        <div className="card h-100">
                                            <div className="card-header py-2 small fw-semibold">
                                                {i18n.t('ub_search_top_sort_fields.text')}
                                            </div>
                                            <div className="card-body p-0">
                                                <TableComponent data={topSearchSortRows} columns={searchTopColumns} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <h6 className="mb-2">{i18n.t('ub_search_recent_logs.text')}</h6>
                                <div className="card mb-3">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={searchData.recentSearchLogs}
                                            columns={recentSearchLogColumns}
                                        />
                                    </div>
                                </div>

                                <h6 className="mb-2">{i18n.t('ub_search_saved_queries_list.text')}</h6>
                                <div className="card">
                                    <div className="card-body p-0">
                                        <TableComponent
                                            data={searchData.savedQueries}
                                            columns={savedSearchQueryColumns}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : null}
                    </Tab.Pane>

                    <Tab.Pane eventKey="transitions">
                        {transitionsError ? <Alert variant="danger">{transitionsError}</Alert> : null}
                        {loadingTransitions ? (
                            <CellLoader />
                        ) : null}
                        {!loadingTransitions && !transitionsError ? (
                            <>
                                <div className="mb-3">
                                    <h6 className="mb-1">{i18n.t('ub_audit_event_transitions_sankey.text')}</h6>
                                    <p className="text-muted small mb-2">
                                        {i18n.t('ub_audit_event_transitions_sankey_hint.text')}
                                    </p>
                                    <div className="card">
                                        <div className="card-body py-3">
                                            <UserAuditAuditEventSankey
                                                nodes={auditEventSankey.nodes}
                                                links={auditEventSankey.links}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {moduleFlows.length > 0 ? (
                                    <div className="mb-3">
                                        <h6 className="mb-2">{i18n.t('ub_flows_by_module.text')}</h6>
                                        <div className="card">
                                            <div className="card-body p-0">
                                                <TableComponent data={moduleFlows} columns={moduleFlowColumns} />
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {endpointFlows.length > 0 ? (
                                    <div className="mb-3">
                                        <h6 className="mb-2">{i18n.t('ub_flows_by_endpoint.text')}</h6>
                                        <div className="card">
                                            <div className="card-body p-0">
                                                <TableComponent data={endpointFlows} columns={endpointFlowColumns} />
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {moduleFlows.length === 0
                                && endpointFlows.length === 0
                                && (auditEventSankey.nodes.length === 0 || auditEventSankey.links.length === 0) ? (
                                    <p className="text-muted mb-0">{i18n.t('ub_no_transitions.text')}</p>
                                ) : null}
                            </>
                        ) : null}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </BaseModal>
    );
});

UserAuditModal.displayName = 'UserAuditModal';

export default UserAuditModal;
